import React, { useEffect, useMemo } from 'react';
import { Box, Paper, TableRow, Typography } from '@material-ui/core';
import { EnumValueDisplay, useObservable } from '@lib/common';
import { propertyQuery, Room, roomsQuery, roomsService } from '@lib/state';
import { LocationType, PropertyDevice } from 'app/state';
import {
  LabelCell,
  SectionHeaderRow,
  StyledTable,
  StyledTableBody,
  ValueCell,
  ValueText,
} from './styled-table';
import { formatValue, identifiersMatch } from './device-detail-tabs-helpers';

interface RoomDetailsTabProps {
  device: PropertyDevice;
  paperClassName: string;
}

export function RoomDetailsTab({ device, paperClassName }: RoomDetailsTabProps) {
  const propertyId = useObservable(propertyQuery.activeProperty)?.id;
  const rooms = (useObservable(roomsQuery.rooms) as Array<Room> | undefined) ?? [];
  const isRoomDevice = device.thingLocationType === LocationType.Room;

  useEffect(() => {
    if (!propertyId || !isRoomDevice || !device.thingIdentifier) return;

    roomsService.getPropertyRoomByNumber(propertyId, device.thingIdentifier);
  }, [propertyId, isRoomDevice, device.thingIdentifier]);

  const matchingRoom = useMemo<Room | undefined>(() => {
    if (!device.thingIdentifier) return undefined;

    return rooms.find(room => identifiersMatch(room.roomNumber, device.thingIdentifier));
  }, [rooms, device.thingIdentifier]);

  return (
    <Paper elevation={4} className={paperClassName}>
      <Box p={2}>
        <Typography variant="h6" gutterBottom>
          Location Information
        </Typography>
        {!isRoomDevice ? (
          <Typography color="textSecondary">This device is not assigned to a room.</Typography>
        ) : !matchingRoom ? (
          <Typography color="textSecondary">
            Room details were not found for this device.
          </Typography>
        ) : (
          <StyledTable>
            <StyledTableBody>
              <SectionHeaderRow>Location</SectionHeaderRow>
              <TableRow hover>
                <LabelCell>Location Type</LabelCell>
                <ValueCell>
                  <ValueText>{formatValue(device.thingLocationType)}</ValueText>
                </ValueCell>
              </TableRow>
              <SectionHeaderRow>Room</SectionHeaderRow>
              <TableRow hover>
                <LabelCell>Room Number (System)</LabelCell>
                <ValueCell>{matchingRoom.roomNumber}</ValueCell>
              </TableRow>
              <SectionHeaderRow>Classification</SectionHeaderRow>
              <TableRow hover>
                <LabelCell>Room Type</LabelCell>
                <ValueCell>{matchingRoom.roomType?.name}</ValueCell>
              </TableRow>
              <TableRow hover>
                <LabelCell>Zone</LabelCell>
                <ValueCell>{matchingRoom.roomZoneName}</ValueCell>
              </TableRow>
              <SectionHeaderRow>Status</SectionHeaderRow>
              <TableRow hover>
                <LabelCell isLastRow>Housekeeping Status</LabelCell>
                <ValueCell isLastRow>
                  <EnumValueDisplay value={matchingRoom.housekeepingStatus} variant="body2" />
                </ValueCell>
              </TableRow>
            </StyledTableBody>
          </StyledTable>
        )}
      </Box>
    </Paper>
  );
}
